import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { AAAStore } from '../../store/root-reducer'
import { selectActiveCallStatus } from '../dashboard/calls-statuses/call-status.selectors'
import { LOCATION_NEXT } from '../location/location.actions'
import { openMessageDialog } from '../ui/ui.actions'
import { MessageDialogTypes } from '../ui/ui.types'
import {
  SERVICE_AVAILABILITY,
  notifyServiceAvailabilityFailure,
  resetAdvisories,
  serviceAvailabilityRequest,
  serviceAvailabilitySuccess,
} from './advisories.actions'
import {
  selectAdvisories,
  selectAdvisoryAcknowledged,
  selectIsDrrEnabled,
} from './advisories.selectors'
import { AdvisoriesService } from './advisories.service'

@Injectable()
export class AdvisoriesEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private errorReportingService: ErrorReportingService,
    private _service: AdvisoriesService
  ) {}

  getServiceAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof serviceAvailabilityRequest>>(SERVICE_AVAILABILITY.REQUEST),
      switchMap((action) =>
        this._service.getServiceAvailability$(action.payload).pipe(
          map((serviceAvailability) =>
            serviceAvailabilitySuccess({
              payload: { serviceAvailability },
            })
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifyServiceAvailabilityFailure
            )
          )
        )
      ),
      catchError((error) =>
        this.errorReportingService.notifyErrorObservable(
          error,
          notifyServiceAvailabilityFailure
        )
      )
    )
  )

  // DRRWEB-3730: Next on the location step requests service-availability again (club lookup ->
  // SET_LOCATION_CLUB.SUCCESS), whether or not the location changed. Clear what the previous
  // response left behind first, so displayAdvisories$ and the navigation gate treat the new
  // response as the first one for this location: the dialog shows the latest messages again.
  // RESET_BREAKDOWN_LOCATION only covers some ways of changing the location, and none of
  // going back and continuing. Skipped on an active call, where no request is sent and the
  // advisory on the tracker must stay. serviceAvailability is cleared too, so a failed request
  // doesn't leave a stale advisory holding the gate with no dialog to dismiss it.
  resetAdvisoriesOnLocationNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOCATION_NEXT),
      withLatestFrom(this.store$.pipe(select(selectActiveCallStatus))),
      filter(([, activeCall]) => activeCall === null),
      map(() => resetAdvisories())
    )
  )

  displayAdvisories$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof serviceAvailabilitySuccess>>(SERVICE_AVAILABILITY.SUCCESS),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectAdvisories)),
            this.store$.pipe(select(selectAdvisoryAcknowledged)),
            this.store$.pipe(select(selectActiveCallStatus)),
            this.store$.pipe(select(selectIsDrrEnabled))
          ),
          // Open the advisory dialog when the club is disabled (blocking, even with
          // no messages) OR there are advisory messages to show, as long as the user
          // hasn't acknowledged it yet. Dedupes on advisoryAcknowledged rather than a
          // one-time "seen" flag, so every retried request that comes back
          // unacknowledged reopens the dialog instead of leaving the user stuck.
          filter(
            ([, advisories, advisoryAcknowledged, activeCall, isDrrEnabled]) =>
              (advisories.length > 0 || !isDrrEnabled) &&
              !advisoryAcknowledged &&
              activeCall === null
          ),
          map(() => openMessageDialog({
            payload: {
              type: MessageDialogTypes.ADVISORY,
            },
          })),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifyServiceAvailabilityFailure
            )
          )
        )
      )
    )
  )
}
